import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { chatAPI, getGuestId } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { 
  GraduationCap, 
  Mail, 
  Phone, 
  MapPin,
  Send,
  ArrowLeft,
  Clock,
  MessageCircle,
  X,
  Loader2
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// WhatsApp Chat Widget Component
const WhatsAppChatWidget = () => {
  const WHATSAPP_LINK = "https://wa.me/qr/M6WGQZJHE55AN1";
  
  const handleOpenWhatsApp = () => {
    window.open(WHATSAPP_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.button
      onClick={handleOpenWhatsApp}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-xl flex items-center justify-center text-white transition-colors group"
      data-testid="whatsapp-chat-btn"
      aria-label="Chat with Admin on WhatsApp"
    >
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      
      {/* Tooltip */}
      <span className="absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        Chat with Admin on WhatsApp
      </span>
    </motion.button>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitting(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="contact-page">
      <SEO 
        title="Contact Us - Get Help with Math, Physics & Chemistry"
        description="Get in touch with Gifted Academy Tutors. Questions about tutoring for Mathematics, Physics, Chemistry, Calculus, or Linear Algebra? Contact us via email, phone, or live chat."
        keywords="contact tutors, tutoring support, math tutoring questions, physics tutoring help, get in touch"
      />
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="rounded-full">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full bg-slate-900 hover:bg-slate-800">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
              Get in Touch
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Have questions about our tutoring services? We're here to help you succeed in your academic journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <motion.div {...fadeUp}>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-indigo-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1">Email Us</h3>
                        <p className="text-slate-600 text-sm mb-2">We'll respond within 24 hours</p>
                        <a href="mailto:contact@giftedacademytutors.ca" className="text-indigo-700 hover:underline">
                          contact@giftedacademytutors.ca
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-green-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1">Call Us</h3>
                        <p className="text-slate-600 text-sm mb-2">Monday to Sunday, 9am to 9pm</p>
                        <a href="tel:+14168465062" className="text-indigo-700 hover:underline font-medium">
                          416-846-5062
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.15 }}>
                <Card className="border-stone-100 border-green-200 bg-green-50 cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.open('https://wa.me/qr/M6WGQZJHE55AN1', '_blank', 'noopener,noreferrer')}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1">Live Chat via WhatsApp</h3>
                        <p className="text-slate-600 text-sm mb-2">Available 24/7</p>
                        <p className="text-green-700 font-medium">Chat with Admin on WhatsApp</p>
                        <p className="text-xs text-slate-500 mt-1">Click to open WhatsApp chat</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-amber-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1">Location</h3>
                        <p className="text-slate-600 text-sm mb-2">Online platform - available worldwide</p>
                        <p className="text-slate-700">Toronto, Ontario, Canada</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-purple-700" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1">Support Hours</h3>
                        <p className="text-slate-600 text-sm mb-2">We're here when you need us</p>
                        <p className="text-slate-700">24/7 Online Support</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div {...fadeUp}>
                <Card className="border-stone-100">
                  <CardContent className="p-8">
                    <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
                      Send us a Message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Your Name</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help you?"
                          required
                          data-testid="contact-subject"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us more about your inquiry..."
                          rows={6}
                          required
                          data-testid="contact-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-auto rounded-full bg-slate-900 hover:bg-slate-800 px-8 py-6 text-lg"
                        data-testid="contact-submit"
                      >
                        {submitting ? (
                          'Sending...'
                        ) : (
                          <>
                            <Send className="h-5 w-5 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Quick answers to common questions
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: "How do I book a tutoring session?",
                a: "Browse our tutors on the Find Tutors page, select one that matches your needs, and contact us to arrange a session. Our admin team will match you with the best available tutor."
              },
              {
                q: "How are tutors selected for Gifted Academy?",
                a: "All tutors are carefully vetted and managed by our admin team. We verify qualifications, experience, and teaching abilities to ensure quality education for our students."
              },
              {
                q: "What subjects do you offer tutoring for?",
                a: "We specialize in STEM subjects including Mathematics, Physics, Chemistry, Calculus, Linear Algebra, Statistics, Computer Science, and more at high school and university levels."
              },
              {
                q: "Can I cancel or reschedule a session?",
                a: "Yes, please contact us via live chat, phone, or email to cancel or reschedule. We recommend giving at least 24 hours notice when possible."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-2">{faq.q}</h3>
                    <p className="text-slate-600">{faq.a}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 bg-stone-50 border-t border-stone-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © 2024 Gifted Academy Tutors. All rights reserved.
          </p>
        </div>
      </footer>

      {/* AI Chat Widget is now rendered globally in App.js */}
    </div>
  );
};

export default ContactPage;
